import { Button, Pagination, Table, Tag, Tooltip } from "@arco-design/web-react";
import type { TableColumnProps } from "@arco-design/web-react";
import { ArrowDown, ArrowUp, Eye, Pencil, Plus, Trash2 } from "lucide-react";

import { formatResourcePointDateTime, formatResourcePointListCoordinates, hasSavedResourcePointGeo } from "../service";
import type {
  ResourcePointSortDirection,
  ResourcePointSortField,
  ResourcePointSpuItem,
} from "../types";

/** 非遗素材画廊模式。 */
export type ResourcePointHeritageGalleryMode = "teaching" | "product";

type ResourcePointSpuTableProps = {
  items: ResourcePointSpuItem[];
  loading: boolean;
  errorMessage: string;
  page: number;
  pageSize: number;
  total: number;
  sortField: ResourcePointSortField;
  sortDirection: ResourcePointSortDirection;
  canManage: boolean;
  canManageSpu: (spu: ResourcePointSpuItem) => boolean;
  canDeleteSpu: (spu: ResourcePointSpuItem) => boolean;
  /** 与当前侧栏菜单一致的实体名称，如企业 / 非遗 / 考察点。 */
  entityLabel?: string;
  showHeritageActions?: boolean;
  showPointTypeColumn?: boolean;
  geoPending: boolean;
  geoVerifyingSpuId?: string;
  onCreateSpu: () => void;
  onEditSpu: (spu: ResourcePointSpuItem) => void;
  onDeleteSpu: (spu: ResourcePointSpuItem) => void;
  onManageSku: (spu: ResourcePointSpuItem) => void;
  onVerifyGeo: (spu: ResourcePointSpuItem) => void;
  onViewHeritageDetail?: (spu: ResourcePointSpuItem) => void;
  onViewHeritageGallery?: (spu: ResourcePointSpuItem, mode: ResourcePointHeritageGalleryMode) => void;
  onSort: (sortField: ResourcePointSortField, sortDirection: ResourcePointSortDirection) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

/** 展示考察点 SPU 主表及行级管理动作。 */
export function ResourcePointSpuTable({
  items,
  loading,
  errorMessage,
  page,
  pageSize,
  total,
  sortField,
  sortDirection,
  canManage,
  canManageSpu,
  canDeleteSpu,
  entityLabel = "考察点",
  showHeritageActions = false,
  showPointTypeColumn = true,
  geoPending,
  geoVerifyingSpuId,
  onCreateSpu,
  onEditSpu,
  onDeleteSpu,
  onManageSku,
  onVerifyGeo,
  onViewHeritageDetail,
  onViewHeritageGallery,
  onSort,
  onPageChange,
  onPageSizeChange,
}: ResourcePointSpuTableProps) {
  const actionWidth = showHeritageActions ? 150 : 120;
  const columns: TableColumnProps<ResourcePointSpuItem>[] = [
    { title: "名称", dataIndex: "pointName", width: 230, ellipsis: true },
    {
      title: "SKU 数量",
      width: 100,
      align: "center",
      render: (_, record) => (
        <div className="resource-point-count-links">
          <button
            type="button"
            className="resource-point-count-link"
            aria-label={`${record.pointName} SKU 数量 ${record.skuCount}`}
            onClick={() => onManageSku(record)}
          >
            <strong>{record.skuCount}</strong>
          </button>
        </div>
      ),
    },    ...(showHeritageActions
      ? [{
          title: "教学 / 产品",
          width: 140,
          align: "center" as const,
          render: (_: unknown, record: ResourcePointSpuItem) => (
            <div className="resource-point-count-links">
              <button
                type="button"
                className="resource-point-count-link"
                aria-label={`查看 ${record.pointName} 教学 ${record.teachingMediaCount}`}
                onClick={() => onViewHeritageGallery?.(record, "teaching")}
              >
                <span>教学</span>
                <strong>{record.teachingMediaCount}</strong>
              </button>
              <button
                type="button"
                className="resource-point-count-link"
                aria-label={`查看 ${record.pointName} 产品 ${record.productMediaCount}`}
                onClick={() => onViewHeritageGallery?.(record, "product")}
              >
                <span>产品</span>
                <strong>{record.productMediaCount}</strong>
              </button>
            </div>
          ),
        }]
      : []),
    ...(showPointTypeColumn
      ? [{ title: "类型", dataIndex: "pointType", width: 130, ellipsis: true }]
      : []),
    {
      title: "地区",
      width: 220,
      render: (_, record) => {
        const region = [record.country, record.province, record.city, record.district]
          .filter(Boolean)
          .join(" / ") || "-";
        const coordinates = formatResourcePointListCoordinates(record);
        return (
          <div className="resource-point-region" aria-label={`${record.pointName} 地区`}>
            <span>{region}</span>
            {coordinates ? <span className="resource-point-region__coords">{coordinates}</span> : null}
          </div>
        );
      },
    },
    {
      title: "GEO",
      width: 90,
      render: (_, record) => {
        const identified = hasSavedResourcePointGeo(record);
        const verifying = geoVerifyingSpuId === record.resourcePointSpuId;
        const tag = (
          <Tag
            color={verifying ? "orangered" : identified ? "green" : "gray"}
            aria-label={canManageSpu(record) ? undefined : `${record.pointName} GEO 状态`}
          >
            {verifying ? "识别中" : identified ? "已识别" : "未识别"}
          </Tag>
        );
        if (!canManageSpu(record)) return tag;
        return (
          <Button
            type="text"
            size="mini"
            className="resource-point-geo-action"
            aria-label={`${record.pointName} GEO 状态`}
            loading={verifying}
            disabled={geoPending || Boolean(geoVerifyingSpuId)}
            onClick={() => onVerifyGeo(record)}
          >
            {tag}
          </Button>
        );
      },
    },
    {
      title: "状态",
      width: 100,
      render: (_, record) => (
        <Tag color={record.isDisabled || record.status !== "active" ? "gray" : "green"}>
          {record.isDisabled ? "停用" : record.status === "active" ? "有效" : "无效"}
        </Tag>
      ),
    },
    {
      title: "行程计数",
      width: 140,
      align: "center",
      render: (_, record) => (
        <div className="resource-point-count-links" aria-label={`${record.pointName} 行程计数`}>
          <div className="resource-point-count-stat">
            <span>未成团</span>
            <strong>{record.itineraryCount}</strong>
          </div>
          <div className="resource-point-count-stat">
            <span>已成团</span>
            <strong>{record.orderItineraryCount}</strong>
          </div>
        </div>
      ),
    },
    {
      title: "是否AI新增",
      width: 110,
      align: "center",
      render: (_, record) => (
        <Tag color={record.isAiCreated ? "arcoblue" : "gray"}>
          {record.isAiCreated ? "AI 创建" : "人工创建"}
        </Tag>
      ),
    },
    {
      title: (
        <ResourcePointSortableHeader
          label="创建时间"
          field="created_at"
          activeField={sortField}
          activeDirection={sortDirection}
          onSort={onSort}
        />
      ),
      width: 190,
      render: (_, record) => formatResourcePointDateTime(record.createdAt),
    },
    {
      title: (
        <ResourcePointSortableHeader
          label="更新时间"
          field="updated_at"
          activeField={sortField}
          activeDirection={sortDirection}
          onSort={onSort}
        />
      ),
      width: 190,
      render: (_, record) => formatResourcePointDateTime(record.updatedAt),
    },
    {
      title: "操作",
      width: actionWidth,
      fixed: "right",
      render: (_, record) => (
        <div className="resource-point-table-actions">
          {showHeritageActions ? (
            <Tooltip content={`查看 ${record.pointName} 详情`}>
              <Button
                type="text"
                size="mini"
                aria-label={`查看 ${record.pointName} 详情`}
                icon={<Eye size={15} />}
                onClick={() => onViewHeritageDetail?.(record)}
              />
            </Tooltip>
          ) : null}
          {canManageSpu(record) ? (
            <Tooltip content={`编辑 ${record.pointName}`}>
              <Button
                type="text"
                size="mini"
                aria-label={`编辑 ${record.pointName}`}
                icon={<Pencil size={15} />}
                onClick={() => onEditSpu(record)}
              />
            </Tooltip>
          ) : null}
          {canDeleteSpu(record) ? (
            <Tooltip content={`删除 ${record.pointName}`}>
              <Button
                type="text"
                size="mini"
                status="danger"
                aria-label={`删除 ${record.pointName}`}
                icon={<Trash2 size={15} />}
                onClick={() => onDeleteSpu(record)}
              />
            </Tooltip>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <section className="resource-point-table-section" aria-label="考察点列表">
      <div className="resource-point-table-toolbar">
        <div>
          <span>共 {total} 个 SPU</span>
        </div>
        {canManage ? (
          <Button type="primary" icon={<Plus size={16} />} onClick={onCreateSpu}>
            {`新增${entityLabel}`}
          </Button>
        ) : null}
      </div>
      {loading ? <div className="resource-point-state">正在加载考察点...</div> : null}
      {errorMessage ? <div className="resource-point-error" role="alert">{errorMessage}</div> : null}
      <Table<ResourcePointSpuItem>
        rowKey="resourcePointSpuId"
        size="small"
        border={{ wrapper: true, cell: false }}
        data={items}
        columns={columns}
        loading={loading}
        pagination={false}
        scroll={{
          x: 1550
            + (showHeritageActions ? 140 : 0)
            - (showPointTypeColumn ? 0 : 130),
        }}
        tableLayoutFixed
        noDataElement="暂无考察点数据"
      />
      {total > 0 ? (
        <div className="resource-point-pagination" data-testid="resource-point-pagination">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            sizeCanChange
            sizeOptions={[20, 50, 100]}
            showTotal
            onChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      ) : null}
    </section>
  );
}

/** 创建/更新时间升降序表头，交互对齐商机与课程线路列表。 */
function ResourcePointSortableHeader({
  label,
  field,
  activeField,
  activeDirection,
  onSort,
}: {
  label: string;
  field: ResourcePointSortField;
  activeField: ResourcePointSortField;
  activeDirection: ResourcePointSortDirection;
  onSort: (field: ResourcePointSortField, direction: ResourcePointSortDirection) => void;
}) {
  const active = activeField === field;
  return (
    <div className="resource-point-sortable-header">
      <span>{label}</span>
      <span className="resource-point-sort-actions">
        <Button
          size="mini"
          shape="circle"
          aria-label={`${label}升序`}
          type={active && activeDirection === "asc" ? "primary" : "secondary"}
          icon={<ArrowUp size={12} />}
          onClick={(event) => {
            event.stopPropagation();
            onSort(field, "asc");
          }}
        />
        <Button
          size="mini"
          shape="circle"
          aria-label={`${label}降序`}
          type={active && activeDirection === "desc" ? "primary" : "secondary"}
          icon={<ArrowDown size={12} />}
          onClick={(event) => {
            event.stopPropagation();
            onSort(field, "desc");
          }}
        />
      </span>
    </div>
  );
}
